import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';
import * as Sentry from '@sentry/browser';
import moment from 'moment';

import { ReactComponent as EditIcon } from '../../../assets/icons/v2/edit-blue.svg';
import { ReactComponent as NotesIcon } from '../../../assets/icons/v2/notes.svg';
import { PrimaryButton } from '../../../components/Button/ActionButtons';
import Table from '../../../components/v2/Table';
import { Title, SubTitle } from '../../../components/v2/Header';
import ReleaseNote, { ReleaseNoteStatus } from '../../../Model/ReleaseNote';
import useComponentMounted from '../../../hooks/useComponentMounted';
import useToast from '../../hooks/useToast';
import { DateFormat } from '../../../utils/date';
import { getStatusTag } from './utils';
import RefineModal from './components/RefineModal';
import {
  PageContainer,
  PageHeader,
  VersionText,
  VersionDate,
  FeaturesText,
} from './styles';
import texts from './texts.json';

const getPublishedLabel = (note) => {
  if (note.iosPublished && note.androidPublished) return texts.published.both;
  if (note.iosPublished) return texts.published.ios;
  if (note.androidPublished) return texts.published.android;
  return texts.published.notYet;
};

const ReleaseNotes = () => {
  const [notes, setNotes] = useState([]);
  const [notesCollection, setNotesCollection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isComponentMounted = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        const releaseNoteCol = ReleaseNote.getAllNotes();
        await releaseNoteCol.fetch();

        if (isComponentMounted.current) {
          setNotesCollection(releaseNoteCol);
          setIsLoading(false);
        }
      } catch (error) {
        Sentry.captureException(error);
        if (isComponentMounted.current) {
          setIsLoading(false);
          showToast(texts.loadError, { error: true });
        }
      }
    };
    init();
  }, [isComponentMounted, showToast]);

  useEffect(() => {
    if (!notesCollection) return () => {};

    const disposer = autorun(() => {
      const { docs } = notesCollection;
      docs.forEach(({ data }) => data);
      setNotes([...docs]);
    });
    return disposer;
  }, [notesCollection]);

  const openModal = useCallback((note) => {
    setSelectedNote(note);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedNote(null);
  }, []);

  const rows = useMemo(() => notes.map((note) => ({
    id: note.id,
    appVersion: note.appVersion,
    generatedDate: note.generatedAt
      ? moment(note.generatedAt.toDate()).format(DateFormat.DEFAULT_DATE_FORMAT)
      : '',
    featuresText: note.features.map((f) => f.title).join(' · '),
    publishedLabel: getPublishedLabel(note),
    status: note.status,
  })), [notes]);

  const columns = useMemo(() => [
    {
      field: 'appVersion',
      headerName: texts.columns.version,
      flex: 1,
      quickSearch: true,
      renderCell: ({ row }) => (
        <div>
          <VersionText>{`v${row.appVersion}`}</VersionText>
          {row.generatedDate && <VersionDate>{row.generatedDate}</VersionDate>}
        </div>
      ),
    },
    {
      field: 'featuresText',
      headerName: texts.columns.features,
      flex: 3,
      quickSearch: true,
      renderCell: ({ row }) => <FeaturesText>{row.featuresText}</FeaturesText>,
    },
    {
      field: 'publishedLabel',
      headerName: texts.columns.published,
      flex: 1,
    },
    {
      field: 'status',
      headerName: texts.columns.status,
      flex: 1,
      renderCell: ({ row }) => getStatusTag(row.status),
    },
    {
      field: 'action',
      headerName: texts.columns.action,
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        const note = notes.find((n) => n.id === row.id);
        const isPending = row.status === ReleaseNoteStatus.PENDING;
        return isPending ? (
          <PrimaryButton variant="info" size="medium" icon={<EditIcon />} onClick={() => openModal(note)}>
            {texts.actions.refine}
          </PrimaryButton>
        ) : (
          <PrimaryButton variant="warning" size="medium" icon={<NotesIcon />} onClick={() => openModal(note)}>
            {texts.actions.view}
          </PrimaryButton>
        );
      },
    },
  ], [notes, openModal]);

  return (
    <PageContainer>
      <PageHeader>
        <Title>{texts.title}</Title>
        <SubTitle>{texts.subtitle}</SubTitle>
      </PageHeader>
      <Table
        rows={rows}
        columns={columns}
        loading={isLoading}
      />
      <RefineModal
        isOpen={isModalOpen}
        note={selectedNote}
        onClose={closeModal}
      />
    </PageContainer>
  );
};

export default compose(
  observer,
)(ReleaseNotes);
